/*
 * Purchase options — writes the chosen plan into the product form.
 *
 * Three cards over one variant, told apart by quantity and selling plan.
 * This replaces the subscription app's own widget as the UI; the app is
 * untouched underneath, because a subscription is a Shopify selling plan
 * and all the cart needs is its id on the line.
 *
 * What it has to set:
 *   quantity      — the input carries form="product-form-…", so it travels
 *                   with the form from outside it. Set its value.
 *   selling_plan  — no such input exists in Dawn; the app used to inject
 *                   one. Created here inside the form instead.
 *
 * Both are dispatched with a change event so the theme's own listeners —
 * price sync, line totals — pick the update up rather than being told twice.
 */
(function () {
  'use strict';

  function fieldset() {
    return document.querySelector('[id^="PurchaseOptions-"]');
  }

  /*
   * The real add-to-cart form, which is not the first one on the page.
   *
   * Shop Pay's installments form carries the same action and the same
   * id prefix and is rendered ahead of it, so `form[action*="/cart/add"]`
   * finds that one — and everything written into it is dropped on the
   * floor, because it never posts to the cart. Dawn wraps only the real
   * form in <product-form>, so that is what identifies it. The fallback
   * covers a theme that has dropped the custom element.
   */
  function form() {
    return (
      document.querySelector('product-form form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]:not([id*="installment"])')
    );
  }

  function quantityInput() {
    return document.querySelector('.quantity__input[name="quantity"]');
  }

  /*
   * Dawn has no selling_plan field, so own one and keep it in the form.
   *
   * Found by our own marker, never by name. The app's radios are named
   * selling_plan too and are injected long after this first runs, so a
   * lookup by name creates ours on the first pass and then, once the app
   * has loaded, starts returning one of the app's instead — every later
   * write lands on that one and ours stays frozen on whatever the first
   * pass happened to set. That is a silent failure: the field is present,
   * the value looks plausible, and every order ships on the wrong interval.
   */
  var PLAN_MARKER = 'data-fme-plan';

  function planInput() {
    var f = form();
    if (!f) return null;
    var input = f.querySelector('input[name="selling_plan"][' + PLAN_MARKER + ']');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.setAttribute(PLAN_MARKER, '');
      f.appendChild(input);
    }
    return input;
  }

  function selectedOption() {
    var root = fieldset();
    return root ? root.querySelector('input[name="purchase_option"]:checked') : null;
  }

  function selectedCadence() {
    var root = fieldset();
    return root ? root.querySelector('input[name="purchase_cadence"]:checked') : null;
  }

  /*
   * Stand the subscription app's own widget down.
   *
   * Hiding it is not enough. Its radios are also named selling_plan, so
   * while they sit in the form the browser submits two values for the same
   * field and which one Shopify keeps is not ours to decide. Disabled
   * controls are never submitted, which is the actual fix; `hidden` is just
   * so it does not flash before this runs.
   *
   * Only the selling_plan inputs are disabled — switching off everything
   * inside the widget risks tripping the app's own scripts, and those
   * inputs are the only ones that reach the cart.
   */
  function standDownAppWidget() {
    document.querySelectorAll('[id^="appstle_subscription_widget"]').forEach(function (widget) {
      widget.setAttribute('hidden', '');
      widget.querySelectorAll('input[name="selling_plan"]').forEach(function (input) {
        input.disabled = true;
      });
    });
  }

  /*
   * Drive the app's own widget to the plan we chose.
   *
   * This is the part that actually decides the cart line, and it took a
   * network trace to see why. The app patches window.fetch, intercepts the
   * add-to-cart on its way out and rewrites selling_plan from its own
   * internal state — so a correct form field is not enough. Posting the
   * same body through an unpatched fetch produced the right plan; posting
   * it through the page produced the app's default every time.
   *
   * That state does not live in the DOM, so setting .checked on its radios
   * changes nothing: only a real click runs the handlers that update it.
   * Hence click(), not checked = true.
   *
   * The subscription radios are named selling_plan_radio and never reach
   * the cart themselves, so they can be left as the click leaves them. The
   * one-time control is named selling_plan, which is the name we submit, so
   * the stand-down is re-asserted afterwards — deferred by a tick so the
   * app's own handlers read it before it goes back to being disabled.
   */
  var appDrivenKey = null;

  function driveAppWidget(plan) {
    var key = plan || 'one-time';
    var target = plan
      ? document.querySelector('input[name="selling_plan_radio"][value="' + plan + '"]')
      : document.querySelector('[id*="appstle"][id*="one_time"] input[name="selling_plan"]');

    if (!target) return;
    if (appDrivenKey === key && target.checked) return;

    var wasDisabled = target.disabled;
    target.disabled = false;
    target.click();
    appDrivenKey = key;

    if (wasDisabled) {
      setTimeout(function () {
        standDownAppWidget();
      }, 0);
    }
  }

  function apply() {
    standDownAppWidget();
    var option = selectedOption();
    if (!option) return;

    var root = fieldset();
    // Only the subscription card carries a plan; the cadence radios decide
    // which one, so read them rather than the card's own data-plan.
    var plan = '';
    if (option.value === 'subscribe') {
      var cadence = selectedCadence();
      plan = cadence ? cadence.value : option.getAttribute('data-plan') || '';
    }

    var quantity = option.getAttribute('data-quantity') || '1';

    var qtyEl = quantityInput();
    if (qtyEl && qtyEl.value !== quantity) {
      qtyEl.value = quantity;
      qtyEl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var planEl = planInput();
    if (planEl) {
      /* One-time options send no field at all rather than an empty one:
         a disabled control is never submitted, which keeps the cart line
         unambiguous instead of relying on how an empty value is read. */
      planEl.disabled = plan === '';
      if (planEl.value !== plan) {
        planEl.value = plan;
        planEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    driveAppWidget(plan);

    if (root) root.setAttribute('data-selected', option.value);
  }

  // Delegated: the fieldset is replaced whenever the section re-renders.
  document.addEventListener('change', function (event) {
    var name = event.target && event.target.name;
    if (name !== 'purchase_option' && name !== 'purchase_cadence') return;

    // Choosing a cadence is also choosing the subscription.
    if (name === 'purchase_cadence') {
      var subscribe = document.querySelector('input[name="purchase_option"][value="subscribe"]');
      if (subscribe && !subscribe.checked) subscribe.checked = true;
    }

    apply();
  });

  /*
   * ?offer= — the shop page picks, this page opens on the pick.
   *
   * A reader who chose "two jars" on the shop page should not have to
   * choose it again here. The shop links carry the offer and this reads
   * it once, before the first apply(), so the card is already open when
   * the page paints rather than switching under the reader a moment
   * later.
   *
   * Unknown or absent values change nothing: the template default wins,
   * which is what a hand-edited URL should land on.
   */
  var OFFER_TO_VALUE = { dual: 'dual', single: 'single-sub', once: 'single' };

  function preselectFromUrl() {
    var root = fieldset();
    if (!root) return;
    var asked;
    try {
      asked = new URLSearchParams(window.location.search).get('offer');
    } catch (e) {
      return;
    }
    if (!asked) return;
    var value = OFFER_TO_VALUE[asked.toLowerCase()];
    if (!value) return;
    var radio = root.querySelector('input[name="purchase_option"][value="' + value + '"]');
    if (!radio || radio.checked) return;
    radio.checked = true;
  }

  function init() {
    if (!fieldset()) return;
    preselectFromUrl();
    apply();
    // The app injects its widget well after load, so the whole pass is
    // re-asserted for a while rather than run once — not just the
    // stand-down: its radios do not exist yet on the first pass, so there
    // is nothing to point at the chosen plan until they arrive. Idempotent.
    [200, 600, 1500, 3000].forEach(function (delay) {
      setTimeout(apply, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The buy-box re-renders on variant change and takes the form with it,
  // which drops the hidden input; re-assert the selection afterwards.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      setTimeout(apply, 0);
    });
  }
})();
